import { useState } from 'react';
import { Button, Stack, Text } from '@chakra-ui/react';
import { useMap } from '../map/map-context';
import { transform } from 'ol/proj';
import { coordinateService } from '@/lib/services/coordinateService';
import { staticPolygonsLayer } from '@/lib/geospatial';
import { TileWMS } from 'ol/source';

export default function HereSearch({ onClose }: { onClose: () => void }) {
  const [error, setError] = useState('');
  const [resultText, setResultText] = useState('');

  const { map, mapResolution, geolocationCoordinates, setGeolocationCoordinates } = useMap();

  const getRadius = () => {
    return Math.floor(mapResolution * 10 || 100);
  };

  const buildUrlFromSource = (source: TileWMS, coordinate: number[]) => {
    const resolution = map?.getView().getResolution();
    const projection = map?.getView().getProjection();
    
    if (!resolution || !projection) {
      throw new Error('Map view not available');
    }
    
    return source.getFeatureInfoUrl(
      coordinate,
      resolution,
      projection,
      { INFO_FORMAT: "application/json" }
    );
  }

  const handleGeolocation = async () => {
    setError('');
    try {
      // Prefer coordinates from context if already available
      let coord3857: number[] | null = null;
      let coord4326: number[] | null = null; // [lon, lat]

      if (geolocationCoordinates.length === 2) {
        coord3857 = geolocationCoordinates;
        coord4326 = transform(coord3857, 'EPSG:3857', 'EPSG:4326');
      } else {
        // Fallback: actively request browser geolocation on click (helps desktop browsers)
        if (!('geolocation' in navigator)) {
          setError('Geolocation is not supported by this browser');
          return;
        }

        const position = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            maximumAge: 0,
            timeout: 10000,
          });
        });

        const lonLat: [number, number] = [position.coords.longitude, position.coords.latitude];
        coord4326 = lonLat;
        coord3857 = transform(lonLat, 'EPSG:4326', 'EPSG:3857');
        // Update context so other components can use the latest position
        setGeolocationCoordinates(coord3857 as number[]);
      }

      if (!coord3857 || !coord4326) {
        setError('Unable to acquire current location');
        return;
      }

      // Center and zoom map to current location
      try {
        const view = map?.getView();
        if (view && coord3857) {
          const currentZoom = view.getZoom() ?? 0;
          const targetZoom = currentZoom < 14 ? 14 : currentZoom;
          view.animate({ center: coord3857 as [number, number], zoom: targetZoom, duration: 600 });
        }
      } catch {
        // Non-fatal
      }

      // Validate location against backend
      const result = await coordinateService.checkCoordinate(coord4326[1], coord4326[0], getRadius());
      if (!result.polygonlayer) {
        setResultText('You do not appear to be located in a soil map unit right now.');
        return;
      }

      // Build GetFeatureInfo URL for the exact map position
      const source = staticPolygonsLayer.getSource() as TileWMS;
      if (!source) {
        setError('Unable to access map source');
        return;
      }

      let url: string;
      try {
        url = buildUrlFromSource(source, coord3857) as string;
        if (!url) {
          setError('Unable to build feature info URL');
          return;
        }
      } catch {
        setError('Unable to build feature info URL');
        return;
      }

      const data = await coordinateService.getPolygonFeatureInfoForUrl(url);
      if (!data?.features || data.features.length === 0) {
        setResultText('You do not appear to be located in a soil map unit right now.');
        return;
      }

      // On success, close without showing a warning/notification here
      onClose();
    } catch (e: any) {
      if (e && typeof e === 'object' && 'code' in e) {
        // GeolocationPositionError codes: 1=PERMISSION_DENIED, 2=POSITION_UNAVAILABLE, 3=TIMEOUT
        switch ((e as GeolocationPositionError).code) {
          case 1:
            setError('Location permission denied. Please allow access and try again.');
            return;
          case 2:
            setError('Location unavailable. Ensure location services are enabled.');
            return;
          case 3:
            setError('Location request timed out. Please try again.');
            return;
        }
      }
      setError('Unable to retrieve your current location.');
    }
  };

  return (
    <Stack gap='4'>
      <Text fontSize='sm' color='gray.600'>
        Use your current location to find soil information
      </Text>

      {error && (
        <Text color='red.500' fontSize='sm'>
          {error}
        </Text>
      )}

      <Button onClick={handleGeolocation} colorScheme='blue'>
        Use my current location
      </Button>

      {resultText && (
        <Text fontSize='xs' color='gray.500'>
          {resultText}
        </Text>
      )}
    </Stack>
  );
}
